/**
 * BOM Detail Page
 *
 * Displays detailed BOM information including:
 * - BOM header info and status
 * - Tree view of materials (left panel)
 * - Materials table (right panel)
 * - Cost breakdown and analysis
 * - Process notes and attachments
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Edit,
  Copy,
  Trash2,
  Loader2,
  FileText,
  Package,
  DollarSign,
  Layers,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  BookOpen,
  ExternalLink,
  Eye,
} from 'lucide-react';
import Layout from '../components/Layout';
import authService from '../services/authService';
import bomService from '../services/bomService';
import productService from '../services/productService';
import FileAttachmentManager from '../components/FileAttachmentManager';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';

function BomDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bom, setBom] = useState(null);
  const [costEstimate, setCostEstimate] = useState(null);
  const [expandedMaterials, setExpandedMaterials] = useState(new Set());
  
  // Product drawer state
  const [productDrawerOpen, setProductDrawerOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState(null);

  // Load user info
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  // Load BOM data
  const loadBom = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const bomData = await bomService.getBom(id);
      setBom(bomData);

      // Load cost estimate
      try {
        const cost = await bomService.getCostEstimate(id, 1);
        setCostEstimate(cost);
      } catch (err) {
        console.error('Failed to load cost estimate:', err);
      }
    } catch (err) {
      console.error('Failed to load BOM:', err);
      setError('Failed to load BOM details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadBom();
  }, [loadBom]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleEdit = () => {
    navigate(`/bom/${id}/edit`);
  };

  const handleCreateVersion = async () => {
    try {
      setLoading(true);
      const newBom = await bomService.createNewVersion(id, {});
      navigate(`/bom/${newBom._id}`);
    } catch (err) {
      setError(`Failed to create new version: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('确定要作废此BOM吗？')) {
      return;
    }

    try {
      setLoading(true);
      await bomService.deleteBom(id);
      navigate('/bom');
    } catch (err) {
      setError(`Failed to delete BOM: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenProductDrawer = async () => {
    if (!bom?.finished_product_id) return;
    
    setProductDrawerOpen(true);
    setProductLoading(true);
    setProductError(null);
    
    try {
      const productData = await productService.getProduct(bom.finished_product_id);
      setSelectedProduct(productData);
    } catch (err) {
      console.error('Failed to load product:', err);
      setProductError('加载产品详情失败');
    } finally {
      setProductLoading(false);
    }
  };

  const handleCloseProductDrawer = () => {
    setProductDrawerOpen(false);
    setSelectedProduct(null);
    setProductError(null);
  };

  const toggleMaterial = (materialId) => {
    setExpandedMaterials((prev) => {
      const next = new Set(prev);
      if (next.has(materialId)) {
        next.delete(materialId);
      } else {
        next.add(materialId);
      }
      return next;
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'default',
      draft: 'secondary',
      obsolete: 'destructive',
    };
    const labels = {
      active: '有效',
      draft: '草稿',
      obsolete: '已作废',
    };
    return (
      <Badge variant={variants[status] || 'secondary'}>
        {labels[status] || status}
      </Badge>
    );
  };

  // Check if processNotes looks like a URL
  const isUrl = (text) => {
    if (!text) return false;
    try {
      new URL(text);
      return true;
    } catch {
      return false;
    }
  };

  // Check permissions
  const canEdit = user && ['admin', 'warehouse_manager', 'engineer'].includes(user.role);

  if (loading && !bom) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !bom) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={handleBack} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回BOM列表
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">{bom?.bom_id}</h1>
                {bom && getStatusBadge(bom.status)}
                {bom?.is_default && <Badge variant="outline">默认</Badge>}
              </div>
              <p className="text-muted-foreground">
                {bom?.finished_product_name} ({' '}
                <button
                  onClick={handleOpenProductDrawer}
                  className="text-primary hover:underline cursor-pointer font-mono"
                  title="点击查看产品详情"
                >
                  {bom?.finished_product_code}
                </button>{' '}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 ml-1"
                  onClick={handleOpenProductDrawer}
                  title="查看产品详情"
                >
                  <Eye className="h-3 w-3" />
                </Button>
                )
              </p>
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleEdit}>
                <Edit className="h-4 w-4 mr-2" />
                编辑
              </Button>
              <Button variant="outline" onClick={handleCreateVersion}>
                <Copy className="h-4 w-4 mr-2" />
                新版本
              </Button>
              <Button
                variant="outline"
                onClick={handleDelete}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                删除
              </Button>
            </div>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* BOM Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">版本</p>
                  <p className="text-2xl font-bold">V{bom?.version || 1}</p>
                </div>
                <FileText className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">产出数量</p>
                  <p className="text-2xl font-bold">{bom?.finished_quantity || 1}</p>
                </div>
                <Package className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">物料数</p>
                  <p className="text-2xl font-bold">{bom?.materials?.length || 0}</p>
                </div>
                <Layers className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">预估成本</p>
                  <p className="text-2xl font-bold">
                    ¥{bom?.estimated_total_cost?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Process Notes */}
        {bom?.process_notes && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                工艺备注 / 知识库链接
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isUrl(bom.process_notes) ? (
                <a
                  href={bom.process_notes}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-primary hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  {bom.process_notes}
                </a>
              ) : (
                <p className="text-muted-foreground whitespace-pre-wrap">{bom.process_notes}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Attachments */}
        {bom?._id && (
          <FileAttachmentManager
            entityType="bom"
            entityId={bom._id}
            title="工艺文档附件"
            maxFiles={20}
            maxSizeMB={50}
          />
        )}

        {/* Main Content - Tree and Table */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tree View (Left Panel) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5" />
                BOM结构
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {/* Root: Finished Product */}
                <div className="flex items-center gap-2 p-2 bg-primary/10 rounded">
                  <Package className="h-4 w-4 text-primary" />
                  <span className="font-medium">{bom?.finished_product_name}</span>
                  <span className="text-sm text-muted-foreground">
                    x{bom?.finished_quantity || 1}
                  </span>
                </div>

                {/* Materials */}
                <div className="ml-4 border-l pl-4 space-y-1">
                  {bom?.materials?.map((material, index) => (
                    <div
                      key={material.line_item_id || index}
                      className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                      onClick={() => toggleMaterial(material.line_item_id || index)}
                    >
                      {expandedMaterials.has(material.line_item_id || index) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <span className="text-sm">{material.material_name}</span>
                      <span className="text-xs text-muted-foreground">
                        x{material.quantity_per} {material.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Materials Table (Right Panel) */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                物料明细
              </CardTitle>
            </CardHeader>
            <CardContent>
              {bom?.materials?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  此BOM未定义物料。
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>物料代码</TableHead>
                      <TableHead>物料名称</TableHead>
                      <TableHead className="text-right">单位用量</TableHead>
                      <TableHead>单位</TableHead>
                      <TableHead className="text-right">损耗率</TableHead>
                      <TableHead className="text-right">预估成本</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bom?.materials?.map((material, index) => (
                      <TableRow key={material.line_item_id || index}>
                        <TableCell className="font-mono">
                          {material.material_product_code}
                        </TableCell>
                        <TableCell>{material.material_name}</TableCell>
                        <TableCell className="text-right">
                          {material.quantity_per}
                        </TableCell>
                        <TableCell>{material.unit}</TableCell>
                        <TableCell className="text-right">
                          {((material.scrap_rate || 0) * 100).toFixed(1)}%
                        </TableCell>
                        <TableCell className="text-right">
                          {material.estimated_cost
                            ? `$${material.estimated_cost.toFixed(2)}`
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Cost Breakdown */}
        {costEstimate && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                成本分析
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Cost Summary */}
                <div className="space-y-4">
                  <h4 className="font-medium">成本汇总</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">材料成本：</span>
                      <span>¥{costEstimate.material_cost?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">人工成本：</span>
                      <span>¥{costEstimate.labor_cost?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">间接费用：</span>
                      <span>¥{costEstimate.overhead_cost?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 font-bold">
                      <span>总成本：</span>
                      <span>¥{costEstimate.total_cost?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                </div>

                {/* Cost Breakdown by Material */}
                <div className="md:col-span-2">
                  <h4 className="font-medium mb-4">材料成本明细</h4>
                  {costEstimate.material_breakdown?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>物料</TableHead>
                          <TableHead className="text-right">数量</TableHead>
                          <TableHead className="text-right">单位成本</TableHead>
                          <TableHead className="text-right">总成本</TableHead>
                          <TableHead className="text-right">占比</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {costEstimate.material_breakdown.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.material_name}</TableCell>
                            <TableCell className="text-right">
                              {item.quantity} {item.unit}
                            </TableCell>
                            <TableCell className="text-right">
                              ¥{item.unit_cost?.toFixed(2) || '0.00'}
                            </TableCell>
                            <TableCell className="text-right">
                              ¥{item.total_cost?.toFixed(2) || '0.00'}
                            </TableCell>
                            <TableCell className="text-right">
                              {item.percentage?.toFixed(1) || '0.0'}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-muted-foreground">
                      暂无成本明细。
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {bom?.notes && (
          <Card>
            <CardHeader>
              <CardTitle>备注</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap">{bom.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Product Detail Drawer */}
        <Sheet open={productDrawerOpen} onOpenChange={setProductDrawerOpen}>
          <SheetContent side="right" className="w-[600px] max-w-[90vw] overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                产品详情
              </SheetTitle>
              <SheetDescription>
                {selectedProduct?.product_id || bom?.finished_product_code}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              {productLoading ? (
                <div className="flex justify-center items-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : productError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{productError}</AlertDescription>
                </Alert>
              ) : selectedProduct ? (
                <div className="space-y-6">
                  {/* Basic Info */}
                  <Card>
                    <CardHeader>
                      <CardTitle>基本信息</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">产品编号</p>
                          <p className="font-mono font-medium">{selectedProduct.product_id}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">分类</p>
                          <p>{selectedProduct.category || '-'}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-sm text-muted-foreground">名称</p>
                          <p className="font-medium">{selectedProduct.name}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-sm text-muted-foreground">规格</p>
                          <p>{selectedProduct.specification || '-'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Manufacturing Parameters */}
                  {(selectedProduct.tip_diameter || selectedProduct.total_length || selectedProduct.degree) && (
                    <Card>
                      <CardHeader>
                        <CardTitle>制造参数</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                          {selectedProduct.tip_diameter && (
                            <div>
                              <p className="text-sm text-muted-foreground">刀尖直径</p>
                              <p className="font-medium">{selectedProduct.tip_diameter} mm</p>
                            </div>
                          )}
                          {selectedProduct.total_length && (
                            <div>
                              <p className="text-sm text-muted-foreground">总长度</p>
                              <p className="font-medium">{selectedProduct.total_length} mm</p>
                            </div>
                          )}
                          {selectedProduct.degree && (
                            <div>
                              <p className="text-sm text-muted-foreground">角度</p>
                              <p className="font-medium">{selectedProduct.degree}°</p>
                            </div>
                          )}
                        </div>
                        {selectedProduct.material && (
                          <div>
                            <p className="text-sm text-muted-foreground">材料</p>
                            <p>{selectedProduct.material}</p>
                          </div>
                        )}
                        {selectedProduct.coating && (
                          <div>
                            <p className="text-sm text-muted-foreground">涂层</p>
                            <p>{selectedProduct.coating}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Status */}
                  <Card>
                    <CardHeader>
                      <CardTitle>状态信息</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">状态</p>
                          <p>
                            {selectedProduct.status === 'active' && (
                              <Badge variant="default">有效</Badge>
                            )}
                            {selectedProduct.status === 'inactive' && (
                              <Badge variant="secondary">停用</Badge>
                            )}
                            {!selectedProduct.status && (
                              <Badge variant="outline">未知</Badge>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">库存状态</p>
                          <p>{selectedProduct.stock_status || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">单位</p>
                          <p>{selectedProduct.unit || '-'}</p>
                        </div>
                        {selectedProduct.client_ref_number && (
                          <div>
                            <p className="text-sm text-muted-foreground">客户参考号</p>
                            <p>{selectedProduct.client_ref_number}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Edit Button */}
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={() => {
                        handleCloseProductDrawer();
                        navigate(`/products/${selectedProduct._id}/edit`);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      编辑产品
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}

export default BomDetailPage;
